import { FormPartComponent, Input as InputType } from "./FormParts";
import { useEffect, useId, useState } from "react";
import { shopVar } from "@/lib/shopStyles";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { DEFAULT_PHONE_COUNTRY, PREFERRED_PHONE_COUNTRIES } from "@/lib/phoneCountry";
import * as m from "@/paraglide/messages.js";

export function FieldLabel({ htmlFor, label, required }: { htmlFor?: string, label: string, required: boolean }) {
    return (
        <label htmlFor={htmlFor} className="block text-sm font-medium leading-6" style={{ color: shopVar('--shop-textColor') }}>
            {label}
            {!required && <span className="ml-1 font-normal opacity-60">({m.field_optional()})</span>}
        </label>
    );
}

export function Input({ formPart, onValueChange, onValidStatusChange, answers, errors }: FormPartComponent<InputType>) {

    if(answers && typeof answers !== 'string') {
        answers = ''
    }

    const [value, setValue] = useState<string>(answers);
    const inputId = useId();
    const describedById = `${inputId}-description`;
    const errorId = `${inputId}-error`;

    // Only the phone field validates its own format, so it is the only one that can
    // surface an error before the API answers. Hold it back until blur — flagging a
    // half-typed number as invalid on every keystroke is just nagging.
    const [phoneBlurred, setPhoneBlurred] = useState(false);
    const [dialCode, setDialCode] = useState('');

    // Initialize date parts from the value if it exists
    const [day, setDay] = useState<string>('');
    const [month, setMonth] = useState<string>('');
    const [year, setYear] = useState<string>('');

    // Initial validation for all fields
    useEffect(() => {
        if (formPart.inputType === 'date') {
            const isValid = !formPart.required || (day.length > 0 && month.length > 0 && year.length > 0);
            onValidStatusChange(formPart.key, isValid);
        } else if (formPart.inputType === 'telephone') {
            onValidStatusChange(formPart.key, validatePhoneField(value || ''));
        } else {
            onValidStatusChange(formPart.key, validateTextField(value || ''));
        }
    }, [formPart.required]);

    // Initialize date values from answers
    useEffect(() => {
        if (formPart.inputType === 'date') {
            const dateValue = answers as string;
            if (dateValue) {
                const [y, m, d] = dateValue.split('-');
                setYear(y ?? '');
                setMonth(m ?? '');
                setDay(d ?? '');
                setValue(dateValue);
            } else {
                setYear('');
                setMonth('');
                setDay('');
                setValue('');
            }
            // Mark as invalid if required and any part is missing
            const dateParts = dateValue ? dateValue.split('-') : [];
            const hasAllParts = dateParts.length === 3 && dateParts.every(part => part.length > 0);
            const isValid = !formPart.required || hasAllParts;
            onValidStatusChange(formPart.key, isValid);
        }
    }, [answers, formPart.key, formPart.required]);

    const validateDateFields = (newDay: string, newMonth: string, newYear: string) => {
        if (!formPart.required) {
            // If not required, it's valid if either all fields are empty or all are filled
            const allEmpty = !newDay && !newMonth && !newYear;
            const allFilled = newDay !== '' && newMonth !== '' && newYear !== '';
            return allEmpty || allFilled;
        }
        // If required, all fields must be filled with non-empty values
        return newDay !== '' && newMonth !== '' && newYear !== '';
    };

    const validateTextField = (text: string) => {
        if (!formPart.required) {
            return true;
        }
        return text.trim().length > 0;
    };

    const handleDateChange = (type: 'day' | 'month' | 'year', newValue: string) => {
        let newDay = day;
        let newMonth = month;
        let newYear = year;

        if (type === 'day') newDay = newValue;
        if (type === 'month') newMonth = newValue;
        if (type === 'year') newYear = newValue;

        // Update the individual field state
        if (type === 'day') setDay(newValue);
        if (type === 'month') setMonth(newValue);
        if (type === 'year') setYear(newValue);

        // Validate and update
        const isValid = validateDateFields(newDay, newMonth, newYear);
        onValidStatusChange(formPart.key, isValid);
        
        // Only update the combined value when all fields are filled
        if (newDay && newMonth && newYear) {
            const dateString = `${newYear}-${newMonth.padStart(2, '0')}-${newDay.padStart(2, '0')}`;
            setValue(dateString);
            onValueChange(formPart.key, dateString);
        } else if (!formPart.required) {
            // If not required and fields are cleared, clear the value
            setValue('');
            onValueChange(formPart.key, '');
        }
    };

    const handleTextChange = (newValue: string) => {
        setValue(newValue);
        onValueChange(formPart.key, newValue);
        onValidStatusChange(formPart.key, validateTextField(newValue));
    };

    // PhoneInput prefills the dial code, so "+31" means the user typed nothing.
    // Treating it as an answer both let required fields submit empty and sent a
    // bare dial code downstream as if it were a phone number.
    const isDialCodeOnly = (phone: string, dialCode: string) => {
        const digits = phone.replace(/\D/g, '');
        return digits.length === 0 || digits === dialCode.replace(/\D/g, '');
    };

    const validatePhoneField = (phone: string) => {
        if (phone === '') {
            return !formPart.required;
        }
        return isValidPhoneNumber(phone);
    };

    const phoneErrorMessage = (phone: string) => {
        if (phone === '') {
            return formPart.required ? m.field_required() : null;
        }
        return isValidPhoneNumber(phone) ? null : m.invalid_phone_format();
    };

    const handlePhoneChange = (phone: string, newDialCode: string) => {
        setValue(phone);
        setDialCode(newDialCode);
        // react-international-phone emits E.164 (e.g. +31612345678) — exactly what the CDP
        // expects for a phone identifier.
        const answer = isDialCodeOnly(phone, newDialCode) ? '' : phone;
        onValueChange(formPart.key, answer);
        onValidStatusChange(formPart.key, validatePhoneField(answer));
    };

    // Memoize these arrays to prevent recreating them on every render
    const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));
    const months = Array.from({ length: 12 }, (_, i) => ({
        value: (i + 1).toString().padStart(2, '0'),
        label: new Intl.DateTimeFormat(undefined, { month: 'long' }).format(new Date(2024, i))
    }));
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => (currentYear - i).toString());

    if (formPart.inputType === 'date') {
        return (
            <div className="grid grid-cols-1 gap-4 pt-4" data-form-part={formPart.key}>
                <FieldLabel label={formPart.label} required={formPart.required} />
                <div className="grid grid-cols-3 gap-4">
                    <select
                        value={day}
                        onChange={(e) => handleDateChange('day', e.target.value)}
                        className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                        style={{
                            color: shopVar('--shop-textColor'),
                            backgroundColor: shopVar('--shop-panelBackgroundColor'),
                            // @ts-expect-error --tw-ring-color is not a valid CSS property
                            '--tw-ring-color': shopVar('--shop-borderColor'),
                        }}
                    >
                        <option value="">Day</option>
                        {days.map(d => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </select>
                    <select
                        value={month}
                        onChange={(e) => handleDateChange('month', e.target.value)}
                        className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                        style={{
                            color: shopVar('--shop-textColor'),
                            backgroundColor: shopVar('--shop-panelBackgroundColor'),
                            // @ts-expect-error --tw-ring-color is not a valid CSS property
                            '--tw-ring-color': shopVar('--shop-borderColor'),
                        }}
                    >
                        <option value="">Month</option>
                        {months.map(m => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </select>
                    <select
                        value={year}
                        onChange={(e) => handleDateChange('year', e.target.value)}
                        className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                        style={{
                            color: shopVar('--shop-textColor'),
                            backgroundColor: shopVar('--shop-panelBackgroundColor'),
                            // @ts-expect-error --tw-ring-color is not a valid CSS property
                            '--tw-ring-color': shopVar('--shop-borderColor'),
                        }}
                    >
                        <option value="">Year</option>
                        {years.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
                {errors.length > 0 && <p className="mt-2 text-sm text-red-600">{errors[0]}</p>}
            </div>
        );
    }

    if (formPart.inputType === 'telephone') {
        const localError = phoneBlurred ? phoneErrorMessage(isDialCodeOnly(value ?? '', dialCode) ? '' : value) : null;
        const shownError = errors[0] ?? localError;

        return (
            <div className="grid grid-cols-1 gap-4 pt-4" data-form-part={formPart.key}>
                <FieldLabel htmlFor={inputId} label={formPart.label} required={formPart.required} />
                {/* The library borders the country button and the input separately, which
                    reads as two controls. Drop both borders and put a single ring on the
                    wrapper so the whole thing focuses as one field. */}
                <div
                    className="flex w-full rounded-md shadow-sm ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset"
                    style={{
                        backgroundColor: shopVar('--shop-panelBackgroundColor'),
                        // @ts-expect-error --tw-ring-color is not a valid CSS property
                        '--tw-ring-color': shownError ? '#dc2626' : shopVar('--shop-borderColor'),
                    }}
                >
                    <PhoneInput
                        defaultCountry={DEFAULT_PHONE_COUNTRY}
                        preferredCountries={PREFERRED_PHONE_COUNTRIES}
                        // Keep the dial code prefilled: with an empty input the library reads
                        // the first digits typed as a country code and silently reselects the
                        // country ("612..." becomes Australia). The prefilled "+31" is not an
                        // answer though — isDialCodeOnly strips it back to '' before we store it.
                        value={value}
                        onChange={(phone, meta) => handlePhoneChange(phone, meta.country.dialCode)}
                        onBlur={() => setPhoneBlurred(true)}
                        placeholder={formPart.placeholder ?? undefined}
                        inputProps={{
                            id: inputId,
                            autoComplete: 'tel',
                            'aria-invalid': shownError ? true : undefined,
                            'aria-describedby': [formPart.description ? describedById : null, shownError ? errorId : null]
                                .filter(Boolean)
                                .join(' ') || undefined,
                        }}
                        className="w-full"
                        inputStyle={{
                            width: '100%',
                            border: 'none',
                            outline: 'none',
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            color: shopVar('--shop-textColor'),
                            // The selector button already spaces the number away from the flag.
                            padding: '6px 12px 6px 4px',
                        }}
                        countrySelectorStyleProps={{
                            buttonStyle: {
                                border: 'none',
                                backgroundColor: 'transparent',
                                // The library button has no padding of its own; without this the
                                // flag sits 3px off the edge while every other field starts at 12px.
                                // 10px, not 12px — the flag image carries ~2px of its own inset.
                                padding: '0 4px 0 10px',
                            },
                            dropdownStyleProps: {
                                style: {
                                    backgroundColor: shopVar('--shop-panelBackgroundColor'),
                                    color: shopVar('--shop-textColor'),
                                    border: `1px solid ${shopVar('--shop-borderColor')}`,
                                },
                                listItemStyle: {
                                    color: shopVar('--shop-textColor'),
                                },
                                listItemDialCodeStyle: {
                                    color: shopVar('--shop-textColor'),
                                    opacity: 0.6,
                                },
                            },
                        }}
                    />
                </div>
                {formPart.description && <p id={describedById} className="text-sm text-gray-500">{formPart.description}</p>}
                {shownError && <p id={errorId} className="mt-2 text-sm text-red-600">{shownError}</p>}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-4 pt-4" data-form-part={formPart.key}>
            <FieldLabel htmlFor={inputId} label={formPart.label} required={formPart.required} />
            <input
                type="text"
                id={inputId}
                value={value}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder={formPart.placeholder ?? undefined}
                aria-invalid={errors.length > 0 ? true : undefined}
                aria-describedby={[formPart.description ? describedById : null, errors.length > 0 ? errorId : null]
                    .filter(Boolean)
                    .join(' ') || undefined}
                className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 placeholder:text-[color:var(--shop-textColor)] placeholder:opacity-60"
                style={{
                    color: shopVar('--shop-textColor'),
                    backgroundColor: shopVar('--shop-panelBackgroundColor'),
                    // @ts-expect-error --tw-ring-color is not a valid CSS property
                    '--tw-ring-color': shopVar('--shop-borderColor'),
                }}
            />
            {formPart.description && <p id={describedById} className="text-sm text-gray-500">{formPart.description}</p>}
            {errors.length > 0 && <p id={errorId} className="mt-2 text-sm text-red-600">{errors[0]}</p>}
        </div>
    );
}